// Client Service for Admin Consultant Accounts Management

import { getCurrentSessionToken } from './auth';

export interface AccountSummary {
  id: string;
  name: string;
  role: 'admin' | 'consultant';
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  structureCount: number;
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getCurrentSessionToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export function generateSuggestedPassword(): string {
  const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
  let res = 'sl-';
  for (let i = 0; i < 6; i++) {
    res += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return res;
}

export function buildInviteShareText(name: string, password: string): string {
  return `Вітаємо, ${name}!
Ваш персональний доступ до Starlife Simulator:
🔗 Посилання: https://app.starlifeodesa.com
🔑 Пароль: ${password}

Збережіть цей пароль для входу. Ваші структури та розрахунки надійно зберігаються у вашому хмарному сховищі.`;
}

export async function getAdminAccounts(): Promise<AccountSummary[]> {
  try {
    const res = await fetch('/api/admin/accounts', {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Помилка завантаження списку консультантів');
    }

    const data = await res.json();
    return data.accounts || [];
  } catch (err: any) {
    console.warn('Failed to fetch admin accounts:', err);
    throw err;
  }
}

export async function createAdminAccount(
  name: string,
  password?: string
): Promise<{ success: boolean; account?: AccountSummary; initialPassword?: string; error?: string }> {
  try {
    const res = await fetch('/api/admin/accounts', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name, password }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Не вдалося створити акаунт' };
    }

    return {
      success: true,
      account: data.account,
      initialPassword: data.initialPassword,
    };
  } catch (err: any) {
    return { success: false, error: err.message || 'Помилка мережі при створенні акаунта' };
  }
}

export async function updateAdminAccount(
  id: string,
  updates: { isActive?: boolean; newPassword?: string; name?: string }
): Promise<{ success: boolean; account?: AccountSummary; updatedPassword?: string; error?: string }> {
  try {
    const res = await fetch('/api/admin/accounts', {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ id, ...updates }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Не вдалося оновити акаунт' };
    }

    return {
      success: true,
      account: data.account,
      updatedPassword: data.updatedPassword,
    };
  } catch (err: any) {
    return { success: false, error: err.message || 'Помилка мережі при оновленні акаунта' };
  }
}

export async function deleteAdminAccount(id: string): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`/api/admin/accounts?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Не вдалося видалити акаунт' };
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message || 'Помилка мережі при видаленні акаунта' };
  }
}
